import axios from 'axios';
import queryString from 'query-string';

import {getApiConfig} from '../config/configProvider';
import {getValidUSDate} from '../helpers/data.helper';
import {GQL_GET_SEARCH, GQL_RELATED_PRODUCTS} from '../gql/search.gql';
import {MATCH_ALL_QUERY} from '../constants/constants';
import {isSearchCorrectionOff} from '../helpers/search.helper';

export default class SearchApi {
  static search(params, headers, host) {
    const config = getApiConfig();
    // fix search api not handling '&' correctly
    const categoryNames =
      params.categoryNames ||
      (!!params.categoryName ? [params.categoryName] : undefined);
    const categoryIds = params.category || params.categoryIds || undefined;
    // `query` is String! in the schema. Any filter-driven browse (curated
    // list, brandOwner, a specific item list via `first`, brand, category,
    // etc.) has no search terms of its own, so send the explicit wildcard —
    // product-search-svc returns a 400 for an empty string, it only accepts
    // '*'. Do NOT widen this to a totally bare search: sending a wildcard
    // with no filter at all changes what a truly empty search returns, and
    // today's undefined (which fails variable coercion) is what callers of a
    // bare search currently rely on to signal "nothing to search for".
    const hasBrowseFilter = !!(
      params.listId ||
      params.brandOwner ||
      params.first ||
      params.brand ||
      categoryIds ||
      categoryNames ||
      params.productFeatures ||
      params.sizeOrCut ||
      params.isNew ||
      params.origin
    );
    let query = params.q ? params.q.replace(/&|%26/g, '-') : params.q;
    if (!params.q) {
      if (params.first && !params.listId) {
        // A campaign banner can pin items with `first` and carry no `q`
        // (/search?first=DAI147%2CDAI005%2CEGG024) — search for the pinned
        // items themselves, which is exactly what a correctly built banner
        // link sends as `q` anyway. No '&' sanitising here: `first` holds
        // item IDs, so unlike free-text `q` it cannot contain one. `listId`
        // keeps precedence — a curated-list browse is already scoped.
        const pinnedIds = []
          .concat(params.first)
          .filter(Boolean)
          .join(',');
        if (pinnedIds) {
          query = pinnedIds;
        } else if (hasBrowseFilter) {
          query = MATCH_ALL_QUERY;
        }
      } else if (hasBrowseFilter) {
        query = MATCH_ALL_QUERY;
      }
    }
    let variables = {
      page: parseInt(params.page, 10),
      size: parseInt(params.size, 10),
      branch: params.b,
      deliveryDate: getValidUSDate(params.deliveryDate),
      brand: params.brand,
      productFeatures: params.productFeatures,
      sizeOrCut: params.sizeOrCut,
      sortType: params.sortType,
      origin: params.origin,
      query,
      categoryIds,
      first: params.first,
    };
    if (categoryNames) {
      variables.categoryNames = categoryNames;
    }
    if (params.listId) {
      variables.listId = params.listId;
    }
    if (params.brandOwner) {
      variables.brandOwner = params.brandOwner;
    }
    if (params.isNew) {
      variables.isNew = params.isNew === 'true';
    }
    if (params.advanced) {
      variables.advanced = true;
    }
    if (params.includeNonPublicItems) {
      variables.includeNonPublicItems = params.includeNonPublicItems === 'true';
    }
    // Spelling correction is on whenever correctedQuery is selected; this is the "Search instead"
    // way back to the words as typed.
    if (isSearchCorrectionOff(params)) {
      variables.correct = false;
    }
    return axios({
      url: host || config.graphQL,
      method: 'post',
      data: {
        query: GQL_GET_SEARCH,
        variables,
      },
      headers,
    }).then(res => res.data);
  }

  static relatedProducts(variables, headers, host) {
    const config = getApiConfig();
    return axios({
      url: host || config.graphQL,
      method: 'post',
      data: {
        query: GQL_RELATED_PRODUCTS,
        variables: {
          ...variables,
          deliveryDate: getValidUSDate(variables.deliveryDate),
        },
      },
      headers,
    }).then(res => res.data);
  }

  static aggregate(params, headers, host) {
    const config = getApiConfig();
    const qs = queryString.stringify(params);
    const url = `${host || config.searchUrl}/product/webaggregate?${qs}`;
    return axios
      .get(url, {headers})
      .then(res => res.data)
      .catch(err => console.log(err));
  }

  static searchSuggestions(keyword) {
    const config = getApiConfig();
    const url = `${config.searchSuggestionUrl}?q=${encodeURIComponent(
      keyword
    )}`;
    return axios
      .get(url)
      .then(res => res.data)
      .catch(err => console.log(err));
  }

  static searchOwnBrandsDescription(keyword) {
    const config = getApiConfig();
    const url = `${config.searchOwnBrandsDescriptionUrl}/${encodeURIComponent(
      keyword
    )}`;
    return axios
      .get(url)
      .then(res => res.data)
      .catch(err => console.log(err));
  }
}
